import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Globe2,
  Layers,
  MessageSquare,
  Mic,
  Monitor,
  PlayCircle,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserRound,
  Users,
  Video,
  Zap,
} from "lucide-react";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  cover_image: string;
  published_at: string;
  read_time: number;
}

const features = [
  {
    icon: Zap,
    title: "One-click meetings",
    benefit: "Start instantly. No downloads.",
    description: "Open the link, allow camera, you're in. Hosts and guests join straight from the browser.",
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: ShieldCheck,
    title: "Private by default",
    benefit: "Your meetings, your data.",
    description: "Encrypted in transit, password-protected rooms, waiting rooms, and granular host controls.",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: Video,
    title: "Crisp video & audio",
    benefit: "Sound clear, look sharp.",
    description: "Adaptive HD video and noise-suppressed audio that hold up on flaky Wi-Fi.",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: Users,
    title: "Team-ready",
    benefit: "Built for real work.",
    description: "Breakout rooms, polls, raise-hand, and webinar mode keep every meeting on track.",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: Mic,
    title: "Recordings & captions",
    benefit: "Never miss a word.",
    description: "Record meetings to the cloud and turn talk into searchable, accurate live captions.",
    image: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=900&q=80",
  },
  {
    icon: Globe2,
    title: "Works everywhere",
    benefit: "Any browser. Any device.",
    description: "Mac, Windows, Linux, iOS, Android - no installs, no plugins, no friction.",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=900&q=80",
  },
];

const workflowSteps = [
  {
    n: "01",
    t: "Create a meeting",
    d: "Click 'Start instant meeting' or schedule one for later. You get a clean, shareable link.",
    metric: "30 sec",
  },
  {
    n: "02",
    t: "Send the link",
    d: "Drop it in chat, email, or your calendar invite. Guests join in their browser, no installs.",
    metric: "1 link",
  },
  {
    n: "03",
    t: "Run a great meeting",
    d: "Use breakout rooms, polls, captions, and recording - all built in, all ready when hosts need them.",
    metric: "Live",
  },
];

const useCases = [
  {
    icon: UserRound,
    audience: "Freelancers",
    headline: "Look professional from day one",
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80",
    points: ["Branded meeting links", "Client-friendly join flow", "Recordings to send afterwards"],
  },
  {
    icon: Users,
    audience: "Remote teams",
    headline: "Run focused team meetings",
    image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=900&q=80",
    points: ["Breakout rooms for sub-teams", "Shared notes and chat", "Calendar invites in one click"],
  },
  {
    icon: Building2,
    audience: "Startups",
    headline: "Scale meetings without the bloat",
    image: "https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=900&q=80",
    points: ["Webinar mode for product launches", "Analytics on attendance", "Affordable per-seat pricing"],
  },
];

const comparison = [
  { capability: "Start a meeting", meethub: "1 click, in browser", legacy: "Install client, sign in, schedule" },
  { capability: "Guest access", meethub: "No account needed", legacy: "Account often required" },
  { capability: "Recording", meethub: "Free up to 1 hour", legacy: "Paid plan only" },
  { capability: "Live captions", meethub: "Included", legacy: "Add-on / paid" },
  { capability: "Branded experience", meethub: "Yes", legacy: "Enterprise only" },
];

const faqs = [
  {
    q: "What is the best professional meeting platform?",
    a: "MeetHub is built for professionals who want meetings that just work. It runs in any modern browser with no downloads, supports HD video and live captions, and includes recording, breakout rooms and webinar mode in every plan.",
  },
  {
    q: "How do I run a smooth online meeting?",
    a: "Send a single link, enable the waiting room, and mute participants on join. MeetHub does this by default and automatically adapts video quality so even guests on weaker connections stay in the call.",
  },
  {
    q: "Can guests join without an account?",
    a: "Yes. Anyone with the meeting link or 12-character code can join from a browser - no signup, no installs, no friction.",
  },
  {
    q: "Is MeetHub free?",
    a: "Yes. The Free plan includes up to 10 meetings per month, 100 participants per meeting, and 1-hour recordings stored for 2 days. Paid plans unlock unlimited meetings and longer recording retention.",
  },
  {
    q: "Is MeetHub secure?",
    a: "All meetings are encrypted in transit. Hosts can set passwords, enable waiting rooms, lock meetings, and remove participants at any time. Recordings are stored privately in your account.",
  },
];

export default function Home() {
  const [latestPosts, setLatestPosts] = useState<BlogPost[]>([]);
  const [blogsLoading, setBlogsLoading] = useState(true);

  useEffect(() => {
    const fetchLatestPosts = async () => {
      try {
        const { data, error } = await supabase
          .from("blog_posts")
          .select("id, slug, title, excerpt, category, cover_image, published_at, read_time")
          .eq("status", "published")
          .order("published_at", { ascending: false })
          .limit(3);

        if (error) throw error;
        setLatestPosts((data as BlogPost[]) || []);
      } catch (error) {
        console.error("Error fetching latest blog posts:", error);
      } finally {
        setBlogsLoading(false);
      }
    };

    fetchLatestPosts();
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "MeetHub",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "120" },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="MeetHub - Online meetings that start in one click"
        description="Start professional video meetings in one click. No downloads. No friction. Built for freelancers, teams, and startups who want simple, reliable online meetings."
        canonicalPath="/"
        jsonLd={jsonLd}
      />
      <Navigation />

      <main>
      <section className="relative pt-24 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
        <div className="absolute inset-0 bg-grid opacity-[0.4] pointer-events-none" aria-hidden />
        <div className="container relative mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="text-center lg:text-left">
              <Badge variant="outline" className="mb-5 bg-card/70 backdrop-blur-sm border-border/80 shadow-xs">
                <Sparkles className="h-3 w-3 mr-1.5 text-primary" />
                New: Instant browser meetings
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                Professional meetings.
                <br />
                <span className="text-gradient-primary">Zero setup for guests.</span>
              </h1>
              <p className="mt-5 text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0">
                Launch calls, webinars, and team syncs with one link. No apps to install, no account required for guests, and no time lost before the meeting starts.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/login">
                  <Button size="lg" className="w-full sm:w-auto h-12 px-7 text-base shadow-md">
                    Start free meeting
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 px-7 text-base">
                    <PlayCircle className="h-4 w-4" />
                    Watch quick demo
                  </Button>
                </Link>
              </div>
              <div className="mt-4 text-xs text-muted-foreground">
                Free forever - No credit card - Works on Chrome, Safari, Edge, and Firefox
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl border bg-card shadow-lg min-h-[440px]">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80"
                alt="Professional team in a productive online meeting"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/35 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                  Live meeting insights
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">A meeting room that feels ready before anyone joins.</h2>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[
                    ["Join time", "< 10 sec"],
                    ["Participants", "100"],
                    ["Captions", "Live"],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur">
                      <p className="text-lg font-semibold">{value}</p>
                      <p className="mt-1 text-xs text-primary-foreground/75">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14 border-y bg-secondary/30">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Badge variant="secondary" className="mb-3">Social proof</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Trusted by modern teams that run meetings every day
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground">
              From client calls to internal standups, teams choose MeetHub for speed, reliability, and a no-friction join experience.
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {["Acme", "Lumen", "Northwind", "Vertex", "Helios", "Quanta"].map((b) => (
              <div key={b} className="rounded-xl border bg-card/70 px-4 py-3 text-center text-sm font-semibold shadow-xs">
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative overflow-hidden rounded-2xl border shadow-sm min-h-[360px]">
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=80"
                alt="Team collaborating in a professional meeting"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-white/90 backdrop-blur px-4 py-3 border">
                <p className="text-sm font-semibold text-foreground">Built for fast decisions and smooth collaboration</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Daily standups, customer calls, and webinars without meeting friction.
                </p>
              </div>
            </div>
            <div>
              <Badge variant="secondary" className="mb-3">Why choose us</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                Why teams choose MeetHub over complex meeting tools
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                MeetHub is designed for teams that want professional online meetings without technical hurdles. You get a frictionless join experience for guests, reliable call quality for global teams, and practical host controls.
              </p>
              <div className="mt-6 grid gap-4">
                {[
                  ["Faster starts, fewer drop-offs", "Browser-first access removes install delays, so participants join quickly and meetings begin on time."],
                  ["Real productivity features, built in", "Captions, recordings, chat, and breakout workflows are available when you need them, without extra tools."],
                  ["Security your clients can trust", "Encrypted sessions, waiting room controls, and host moderation tools help protect every conversation."],
                ].map(([title, text]) => (
                  <div key={title} className="rounded-xl border bg-card p-4 hover:border-primary/30 hover:shadow-sm transition-all">
                    <h3 className="font-semibold">{title}</h3>
                    <p className="text-sm text-muted-foreground mt-1.5">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
            <div className="sticky top-28 rounded-2xl border bg-gradient-soft p-6 sm:p-8 shadow-sm">
              <Badge variant="outline" className="mb-4">Everything you need</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Professional meetings that just work</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Less setup. Fewer settings. More productive conversations. The core experience stays simple while the tools stay powerful.
              </p>
              <div className="mt-6 overflow-hidden rounded-xl border bg-card">
                <img
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1000&q=80"
                  alt="Team using professional video meeting tools"
                  className="h-56 w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {features.map((f, index) => (
                <Card key={f.title} className={`group overflow-hidden border-border/70 hover:border-primary/30 hover:shadow-lg transition-all ${index === 0 ? "sm:col-span-2" : ""}`}>
                  <div className="relative h-36 overflow-hidden bg-secondary">
                    <img src={f.image} alt={`${f.title} preview`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/25 to-transparent" />
                    <div className="absolute left-4 bottom-4 h-11 w-11 rounded-lg bg-background/90 text-primary shadow-sm inline-flex items-center justify-center backdrop-blur">
                      <f.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-lg">{f.title}</h3>
                    <p className="text-sm font-medium text-primary mt-1">{f.benefit}</p>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
            <div>
              <Badge variant="secondary" className="mb-3">How it works</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">From link to live in 3 steps</h2>
              <div className="mt-8 space-y-4">
                {workflowSteps.map((s, index) => (
                  <div key={s.n} className="relative flex gap-4 rounded-2xl border bg-card p-5 hover:border-primary/30 hover:shadow-md transition-all">
                    {index < workflowSteps.length - 1 && <span className="absolute left-9 top-16 h-8 w-px bg-border" aria-hidden />}
                    <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-sm font-semibold">{s.n}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <h3 className="font-semibold text-lg">{s.t}</h3>
                        <span className="text-xs font-medium text-primary">{s.metric}</span>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[500px]">
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1100&q=80"
                alt="Meeting workflow on a laptop"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/35 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                  Browser-first
                </Badge>
                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">No install flow. No awkward waiting room confusion.</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Built for serious work</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">One platform. Three kinds of teams.</h2>
          </div>
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
            <Card className="group overflow-hidden border-border/70 hover:border-primary/30 hover:shadow-lg transition-all">
              <div className="relative h-72 overflow-hidden">
                <img src={useCases[0].image} alt={useCases[0].audience} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-primary-foreground">
                  <Badge className="mb-3 bg-background/90 text-foreground hover:bg-background/90">{useCases[0].audience}</Badge>
                  <h3 className="text-2xl font-bold">{useCases[0].headline}</h3>
                </div>
              </div>
              <CardContent className="p-6">
                <ul className="grid sm:grid-cols-3 gap-3">
                  {useCases[0].points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <div className="grid gap-6">
              {useCases.slice(1).map((u) => (
                <Card key={u.audience} className="overflow-hidden border-border/70 bg-card hover:border-primary/30 hover:shadow-md transition-all">
                  <CardContent className="p-0">
                    <div className="grid sm:grid-cols-[160px_1fr]">
                      <img src={u.image} alt={u.audience} className="h-48 sm:h-full w-full object-cover" loading="lazy" />
                      <div className="p-5">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center mb-3">
                          <u.icon className="h-5 w-5" />
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-primary">{u.audience}</p>
                        <h3 className="mt-1 font-semibold text-lg">{u.headline}</h3>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {u.points.map((p) => (
                            <span key={p} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">{p}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="rounded-2xl border bg-foreground text-background overflow-hidden shadow-lg">
            <div className="grid lg:grid-cols-[0.85fr_1.15fr]">
              <div className="p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-background/15">
                <Badge className="mb-5 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                  Why switch
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">MeetHub vs traditional tools</h2>
                <p className="mt-4 text-sm sm:text-base text-background/75 leading-relaxed">
                  Modern meetings without legacy complexity, confusing client installs, or hidden basics.
                </p>
                <div className="mt-8 grid grid-cols-2 gap-3">
                  {["No installs", "Guest friendly", "Captions included", "Branded links"].map((item) => (
                    <div key={item} className="rounded-xl border border-background/15 bg-background/5 px-4 py-3 text-sm font-medium">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-background text-foreground p-5 sm:p-6 lg:p-8">
                <div className="space-y-3">
                  {comparison.map((row) => (
                    <div key={row.capability} className="rounded-xl border bg-card p-4">
                      <p className="text-xs font-semibold uppercase tracking-widest text-primary">{row.capability}</p>
                      <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        <div className="rounded-lg bg-primary/10 p-3">
                          <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                            <CheckCircle2 className="h-4 w-4" />
                            MeetHub
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">{row.meethub}</p>
                        </div>
                        <div className="rounded-lg bg-secondary/50 p-3">
                          <p className="text-sm font-semibold">Traditional tools</p>
                          <p className="mt-1 text-sm text-muted-foreground">{row.legacy}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
            <div className="rounded-2xl border bg-card p-6 sm:p-8 shadow-sm">
              <Badge variant="outline" className="mb-4">Answers</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Frequently asked questions</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Quick answers for teams evaluating MeetHub before their first call, client meeting, or webinar.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  [Monitor, "No installs"],
                  [ShieldCheck, "Secure rooms"],
                  [Layers, "Built-in tools"],
                  [TrendingUp, "Scales up"],
                ].map(([Icon, label]) => (
                  <div key={label as string} className="rounded-xl border bg-secondary/50 px-4 py-3 text-sm font-medium">
                    <Icon className="h-4 w-4 text-primary mb-2" />
                    {label as string}
                  </div>
                ))}
              </div>
            </div>
            <Card className="shadow-sm">
              <CardContent className="p-5 sm:p-6">
                <Accordion type="single" collapsible className="space-y-2">
                  {faqs.map((faq, i) => (
                    <AccordionItem key={i} value={`q-${i}`} className="rounded-xl border px-4">
                      <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">{faq.q}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">Latest insights</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Latest from the MeetHub blog</h2>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Fresh ideas on productive meetings, team communication, security, and better workflows.
              </p>
            </div>
            <Link to="/blog">
              <Button variant="outline" className="h-11 px-5">
                Read more blogs <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          {blogsLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((item) => (
                <div key={item} className="h-80 rounded-2xl border bg-secondary/40 animate-pulse" />
              ))}
            </div>
          ) : latestPosts.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="h-full">
                  <Card className="group h-full overflow-hidden border-border/70 hover:border-primary/30 hover:shadow-lg transition-all">
                    <div className="relative h-48 overflow-hidden bg-secondary">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/25 to-transparent" />
                      <Badge className="absolute left-4 top-4 bg-background/90 text-foreground hover:bg-background/90">
                        {post.category}
                      </Badge>
                    </div>
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {format(new Date(post.published_at), "MMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {post.read_time} min read
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-2">{post.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border bg-gradient-soft p-8 text-center">
              <h3 className="text-xl font-semibold">Blog posts are coming soon.</h3>
              <p className="mt-2 text-muted-foreground">Visit the blog page for future product updates and meeting guides.</p>
            </div>
          )}
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container">
          <Card className="relative overflow-hidden border-0 bg-gradient-primary text-primary-foreground shadow-lg">
            <div className="absolute inset-0 bg-grid opacity-20" aria-hidden />
            <CardContent className="relative px-6 py-14 sm:p-16 text-center">
              <Briefcase className="h-10 w-10 mx-auto mb-4 opacity-90" />
              <h2 className="text-3xl sm:text-4xl font-bold">Your next meeting starts in one click.</h2>
              <p className="mt-3 text-base sm:text-lg opacity-90 max-w-xl mx-auto">
                Free forever for individuals. Built to scale with your team.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/login">
                  <Button size="lg" variant="secondary" className="h-12 px-7 text-base">
                    Start free
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-12 px-7 text-base bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10"
                  >
                    See pricing
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
}


